<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <Table
    :columns="columns"
    :data-source="tableData"
    :loading="isLoading"
    :pagination="false"
    :row-key="rowKey"
    :row-selection="showRowSelection ?
      {
        selectedRowKeys: selectedRowKeysState,
        onChange: onSelectChange,
        type: selectionType,
        getCheckboxProps: getCheckboxProps,
        preserveSelectedRowKeys: preserveSelectedRowKeys
      }: undefined"
    :scroll="{ x: '100%', y: tableHeight }"
    @change="onChangeTable"
    @resize-column="handleResizeColumn"
  >
    <template v-for="item in slotKeys" #[item]="scope">
      <slot :name="item" v-bind="scope" />
    </template>
    <template v-if="needSummary" #summary>
      <a-table-summary fixed="top">
        <a-table-summary-row>
          <a-table-summary-cell v-for="(item, idx) in columns" :key="item" :index="idx">
            {{ totalLine[item.dataIndex] ?? '-' }}
          </a-table-summary-cell>
        </a-table-summary-row>
      </a-table-summary>
    </template>
  </Table>
  <div v-show="showPagination && pagination.total > pagination.pageSize" class="pagination-container">
    <Pagination
      :current="pagination.page"
      :page-size="pagination.pageSize"
      :page-size-options="pageSizeOptions"
      show-quick-jumper
      show-size-changer
      :show-total="total => `共${total}条`"
      :total="pagination.total"
      @change="onChangePage"
      @show-size-change="onShowSizeChange"
    />
  </div>
</template>

<script setup lang="ts">
import type { TableColumnsType } from 'ant-design-vue'
import { type PropType, ref, useSlots, watch, defineProps, defineEmits } from 'vue'
import { type Key, type MyObject, type Pagination as PaginationType } from './types'
import { type TablePaginationConfig } from 'ant-design-vue/es/table/Table'
import { type ColumnType, type FilterValue, type SorterResult } from 'ant-design-vue/es/table/interface'
import {
  Table,
  Pagination,

} from "ant-design-vue";

const props = defineProps({
  needSummary: {
    type: Boolean,
    default: false,
  },
  totalLine: {
    type: Object as PropType<MyObject>,
    default: () => ({}),
  },
  tableHeight: {
    type: [String, Number],
    default: '100%',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  tableData: {
    type: Array,
    default: () => [],
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  columns: {
    type: Array as PropType<TableColumnsType>,
    default: () => [],
  },
  showRowSelection: {
    type: Boolean,
    default: false,
  },
  preserveSelectedRowKeys: {
    type: Boolean,
    default: true,
  },
  rowKey: {
    type: String,
    default: 'id',
  },
  selectionType: {
    type: String,
    default: 'checkbox',
  },
  selectedRowKeys: {
    type: Array as PropType<number[] | string[]>,
    default: () => ([]),
  },
  pagination: {
    type: Object as PropType<PaginationType>,
    default: () => ({}),
  },
})
const emits = defineEmits([
  'changePage', 'changeTable', 'selectChange', 'updatePagination',
])
const pageSizeOptions = ref<string[]>(['10', '20', '50', '100', '200'])
const selectedRowKeysState = ref<Key[]>()

const getCheckboxProps = (record: { disabled: boolean }) => {
  return {
    disabled: record.disabled,
  }
}

const onShowSizeChange = (_current: number, pageSize: number) => {
  Object.assign(props.pagination, {
    pageSize,
  })
}

watch(() => props.selectedRowKeys, (arr) => {
  selectedRowKeysState.value = arr
}, {
  immediate: true,
  deep: true,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSelectChange = (selectedRowKeys: Key[], selectedRows: any[]) => {
  selectedRowKeysState.value = selectedRowKeys

  emits('selectChange', selectedRowKeys, selectedRows)
}
const slots = useSlots()

const slotKeys = Object.keys(slots)

const onChangePage = (page: number, pageSize: number) => {
  emits('changePage', page, pageSize)
}

const onChangeTable = (
  pagination: TablePaginationConfig,
  filters: Record<string, FilterValue | null>,
  sorter: SorterResult,
) => {
  emits('changeTable', sorter, pagination, filters)
}

const handleResizeColumn = (w: number, col: ColumnType) => {
  col.width = w
}

</script>

<style scoped lang="less">
.pagination-container {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  padding-bottom: 20px;
  background-color: #fff;
}
</style>
